// T061 -- Transaction submission status Zustand store.

import type { TxStage } from '@morfi/backend-sdk-v2/client';
import type { StoreApi, UseBoundStore } from 'zustand';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

const STALE_THRESHOLD_MS: number = 5 * 60 * 1000; // 5 minutes

interface TxErrorEntry {
  code: string;
  userMessageKey: string;
}

interface SubmissionEntry {
  stage: TxStage;
  /** On-chain instruction (e.g. `trade`, `claim_prize`) — lets trade-only UI skip claims. */
  instructionName: string;
  signature?: string;
  error?: TxErrorEntry;
  traceId: string;
  attempt: number;
  maxAttempts: number;
  startedAt: number;
}

interface TxSubmissionState {
  submissions: Record<string, SubmissionEntry>;
  startSubmission: (traceId: string, instructionName: string, maxAttempts?: number) => void;
  updateStage: (traceId: string, stage: TxStage, extra?: { attempt?: number }) => void;
  setSignature: (traceId: string, signature: string) => void;
  setError: (traceId: string, error: TxErrorEntry) => void;
  clearSubmission: (traceId: string) => void;
  clearOldSubmissions: () => void;
}

export const useTxSubmissionStore: UseBoundStore<StoreApi<TxSubmissionState>> =
  createTrackedStore<TxSubmissionState>('useTxSubmissionStore')((set, get) => ({
    submissions: {},

    startSubmission(traceId: string, instructionName: string, maxAttempts: number = 3): void {
      // Clear old submissions first
      get().clearOldSubmissions();

      set((state) => ({
        submissions: {
          ...state.submissions,
          [traceId]: {
            stage: 'preflight',
            instructionName,
            traceId,
            attempt: 1,
            maxAttempts,
            startedAt: Date.now(),
          },
        },
      }));
    },

    updateStage(traceId: string, stage: TxStage, extra?: { attempt?: number }): void {
      set((state) => {
        const entry = state.submissions[traceId];
        if (!entry) return state;
        return {
          submissions: {
            ...state.submissions,
            [traceId]: {
              ...entry,
              stage,
              ...(extra?.attempt !== undefined ? { attempt: extra.attempt } : {}),
            },
          },
        };
      });
    },

    setSignature(traceId: string, signature: string): void {
      set((state) => {
        const entry = state.submissions[traceId];
        if (!entry) return state;
        return {
          submissions: {
            ...state.submissions,
            [traceId]: { ...entry, signature },
          },
        };
      });
    },

    setError(traceId: string, error: TxErrorEntry): void {
      set((state) => {
        const entry = state.submissions[traceId];
        if (!entry) return state;
        return {
          submissions: {
            ...state.submissions,
            [traceId]: { ...entry, error, stage: 'failed' },
          },
        };
      });
    },

    clearSubmission(traceId: string): void {
      set((state) => {
        const { [traceId]: _, ...rest } = state.submissions;
        return { submissions: rest };
      });
    },

    clearOldSubmissions(): void {
      const now = Date.now();
      set((state) => {
        const fresh: Record<string, SubmissionEntry> = {};
        for (const [id, entry] of Object.entries(state.submissions)) {
          if (entry && now - entry.startedAt < STALE_THRESHOLD_MS) {
            fresh[id] = entry;
          }
        }
        return { submissions: fresh };
      });
    },
  }));
